import { useCallback, useEffect, useRef, useState } from 'react';
import { portfolioService } from '../services/portfolioService';

// Explicit context handoff revalidates publication; displayed strings are never a fallback.
export function usePublicChatContext({ locale, revision, selection, enabled, refresh, onDraft }) {
  const request = useRef();
  const [state, setState] = useState({ loading: false, error: false });
  useEffect(() => {
    setState({ loading: false, error: false });
    return () => { request.current?.abort(); };
  }, [locale, selection, enabled]);
  useEffect(() => {
    setState((previous) => ({ ...previous, loading: false }));
    return () => { request.current?.abort(); };
  }, [revision]);

  const prepare = useCallback(async (materialId, sectionId = null) => {
    request.current?.abort();
    if (!enabled) return;
    const controller = new AbortController();
    request.current = controller;
    const owns = () => request.current === controller && !controller.signal.aborted;
    const coherent = (value) => value?.contractVersion === 1 && value.locale === locale &&
      value.contentRevision === revision;
    setState({ loading: true, error: false });
    try {
      const before = await portfolioService.bootstrap(locale, controller.signal);
      if (!owns()) return;
      if (!coherent(before) || !before.materials.some((item) => item.id === materialId))
        throw new Error('Publication changed');
      const detail = await portfolioService.material(materialId, locale, controller.signal);
      if (!owns()) return;
      if (!coherent(detail) || detail.material?.id !== materialId)
        throw new Error('Publication changed');
      let draft = `${detail.material.title}\n\n${detail.material.summary}`;
      if (sectionId) {
        if (!detail.material.sections.some((item) => item.id === sectionId))
          throw new Error('Section unavailable');
        const section = await portfolioService.section(materialId, sectionId, locale, controller.signal);
        if (!owns()) return;
        if (!coherent(section) || section.materialId !== materialId || section.section?.id !== sectionId)
          throw new Error('Publication changed');
        draft = `${detail.material.title}\n${section.section.title}\n\n${section.section.body}`;
      }
      const after = await portfolioService.bootstrap(locale, controller.signal);
      if (!owns()) return;
      if (!coherent(after) || !after.materials.some((item) => item.id === materialId))
        throw new Error('Publication changed');
      setState({ loading: false, error: false });
      onDraft(draft);
    } catch (_) {
      if (!owns()) return;
      setState({ loading: false, error: true });
      refresh();
    }
  }, [enabled, locale, revision, refresh, onDraft]);
  return { ...state, prepare };
}
